// German umlauts and ß expand to their conventional two-letter forms
// (ü→ue, not ü→u) before the generic diacritic strip below.
const GERMAN_TRANSLITERATIONS: Record<string, string> = {
	ä: "ae",
	ö: "oe",
	ü: "ue",
	ß: "ss",
}

// Cyrillic and Greek romanization, covering Russian, Ukrainian, Kazakh and
// Greek. Without it the strip below eats the whole title and every article in
// these languages lands on the same "untitled" url.
const CYRILLIC_TRANSLITERATIONS: Record<string, string> = {
	а: "a",
	б: "b",
	в: "v",
	г: "g",
	д: "d",
	е: "e",
	ё: "e",
	ж: "zh",
	з: "z",
	и: "i",
	й: "i",
	к: "k",
	л: "l",
	м: "m",
	н: "n",
	о: "o",
	п: "p",
	р: "r",
	с: "s",
	т: "t",
	у: "u",
	ф: "f",
	х: "kh",
	ц: "ts",
	ч: "ch",
	ш: "sh",
	щ: "shch",
	ъ: "",
	ы: "y",
	ь: "",
	э: "e",
	ю: "yu",
	я: "ya",
	// Ukrainian
	і: "i",
	ї: "i",
	є: "ie",
	ґ: "g",
	// Kazakh
	ә: "a",
	ғ: "g",
	қ: "q",
	ң: "ng",
	ө: "o",
	ұ: "u",
	ү: "u",
	һ: "h",
}

const GREEK_TRANSLITERATIONS: Record<string, string> = {
	α: "a",
	β: "v",
	γ: "g",
	δ: "d",
	ε: "e",
	ζ: "z",
	η: "i",
	θ: "th",
	ι: "i",
	κ: "k",
	λ: "l",
	μ: "m",
	ν: "n",
	ξ: "x",
	ο: "o",
	π: "p",
	ρ: "r",
	σ: "s",
	ς: "s",
	τ: "t",
	υ: "y",
	φ: "f",
	χ: "ch",
	ψ: "ps",
	ω: "o",
}

const NON_LATIN_TRANSLITERATIONS: Record<string, string> = {
	...CYRILLIC_TRANSLITERATIONS,
	...GREEK_TRANSLITERATIONS,
}

const mapChars = (input: string, table: Record<string, string>): string =>
	Array.from(input)
		.map((char) => table[char] ?? char)
		.join("")

// Transliterate accented/non-ASCII letters to ASCII so slugs stay readable
// ("für lehrkräfte" → "fuer-lehrkraefte", not "f-r-lehrkr-fte"). German expands
// before the accent strip so ü becomes ue rather than u; Cyrillic and Greek map
// after it, since an accented vowel like ά only matches once the tonos is gone.
const transliterate = (input: string): string =>
	mapChars(
		// NFC first, or a decomposed Ü misses the umlaut table and degrades to u
		// while the composed one becomes ue, giving one title two slugs.
		mapChars(input.toLowerCase().normalize("NFC"), GERMAN_TRANSLITERATIONS)
			.normalize("NFD")
			.replace(/[̀-ͯ]/g, ""),
		NON_LATIN_TRANSLITERATIONS,
	)

export const generateHeadlineKey = (
	headline: string,
	index: number,
): string => {
	return `${index}-${transliterate(headline.trim())
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/(^-|-$)/g, "")}`
}

// The slug text a title yields, or "" when it yields none. Callers decide what
// an empty result means; inventing a name here is what put a whole workspace's
// articles on /untitled, /untitled-2, /untitled-3.
//
// Letters of any script survive, so a title we cannot romanize keeps its own
// characters (/docs/認証) instead of being thrown away. NFC last so two slugs
// that look identical cannot differ by composition and both pass the unique
// index.
export const slugifySegment = (text?: string | null): string =>
	transliterate(text ?? "")
		.replace(/[^\p{L}\p{N}]+/gu, "-")
		.replace(/(^-|-$)/g, "")
		.normalize("NFC")

export const generateUrlName = (title?: string): string =>
	slugifySegment(title) || "untitled"

export function formatDateInTimezone(date: Date, timezone: string): string {
	try {
		const formatter = new Intl.DateTimeFormat("en-CA", {
			timeZone: timezone,
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
		})
		return formatter.format(date)
	} catch {
		return date.toISOString().split("T")[0]!
	}
}

// Long-form date for the changelog email header, in the workspace's timezone.
export function formatChangelogDisplayDate(
	date: Date,
	timezone = "Europe/Berlin",
): string {
	try {
		return new Intl.DateTimeFormat("en-US", {
			timeZone: timezone,
			year: "numeric",
			month: "long",
			day: "numeric",
		}).format(date)
	} catch {
		return new Intl.DateTimeFormat("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
		}).format(date)
	}
}

export function generateChangelogSlug(
	date: Date | null,
	title: string,
	createdAt?: Date,
	timezone = "Europe/Berlin",
): string {
	const effectiveDate = date ?? createdAt ?? new Date()
	const dateStr = formatDateInTimezone(effectiveDate, timezone)
	const titleSlug = generateUrlName(title)
	return `${dateStr}-${titleSlug}`
}

const IMG_TAG_RE = /<img\b[^>]*>/gi

const attributeValue = (tag: string, name: string): string => {
	const match = new RegExp(`\\b${name}\\s*=\\s*"([^"]*)"`, "i").exec(tag)
	return match?.[1]?.trim() ?? ""
}

export const extractImageSrcs = (content: string): string[] => {
	const srcs: string[] = []
	for (const tag of content.match(IMG_TAG_RE) ?? []) {
		const src = attributeValue(tag, "src")
		if (src && !srcs.includes(src)) srcs.push(src)
	}
	return srcs
}

const IFRAME_TAG_RE = /<iframe\b[^>]*>/gi
const FILE_EMBED_RE = /<div\b[^>]*\bdata-file-embed\b[^>]*>[\s\S]*?<\/div>/gi

export interface DocumentEmbed {
	src: string
	fileName: string
}

const isPdfPath = (src: string) => /\.pdf(?:$|[?#])/i.test(src)

const fileNameFromSrc = (src: string) =>
	decodeURIComponent(src.split(/[?#]/)[0]?.split("/").pop() ?? "") || "Document"

function pdfIframe(tag: string): DocumentEmbed | null {
	const src = attributeValue(tag, "src")
	if (!src || (!/\bdata-pdf\b/i.test(tag) && !isPdfPath(src))) return null
	return { src, fileName: attributeValue(tag, "title") || fileNameFromSrc(src) }
}

function fileEmbed(block: string): DocumentEmbed & { isPdf: boolean } {
	const src = attributeValue(block, "data-file-src")
	return {
		src,
		fileName: attributeValue(block, "data-file-name") || fileNameFromSrc(src),
		isPdf:
			attributeValue(block, "data-file-type") === "application/pdf" ||
			isPdfPath(src),
	}
}

/** PDFs embedded in an article, either as a viewer iframe or as an attached file. */
export const extractPdfEmbeds = (content: string): DocumentEmbed[] => {
	const embeds: DocumentEmbed[] = []
	for (const tag of content.match(IFRAME_TAG_RE) ?? []) {
		const pdf = pdfIframe(tag)
		if (pdf) embeds.push(pdf)
	}
	for (const block of content.match(FILE_EMBED_RE) ?? []) {
		const { isPdf, ...embed } = fileEmbed(block)
		if (isPdf && embed.src) embeds.push(embed)
	}
	return embeds.filter(
		(embed, index) => embeds.findIndex((e) => e.src === embed.src) === index,
	)
}

const documentMarker = (fileName: string, text?: string) =>
	text ? ` [document: ${fileName}] ${text} ` : ` [document: ${fileName}] `

// An <img> carries the only description of what a screenshot shows, so it is
// folded into the text instead of being stripped with the rest of the tags.
// A document is folded in as its text, never its file url, so the agent
// answers from it instead of linking it.
export const formatHtmlContentForSearch = (
	content: string,
	descriptionBySrc?: Map<string, string>,
	documentTextBySrc?: Map<string, string>,
): string => {
	return content
		.replaceAll(FILE_EMBED_RE, (block) => {
			const { src, fileName } = fileEmbed(block)
			return documentMarker(fileName, documentTextBySrc?.get(src))
		})
		.replaceAll(IFRAME_TAG_RE, (tag) => {
			const pdf = pdfIframe(tag)
			if (pdf)
				return documentMarker(pdf.fileName, documentTextBySrc?.get(pdf.src))
			const src = attributeValue(tag, "src")
			return src ? ` ${src} ` : " "
		})
		.replaceAll(IMG_TAG_RE, (tag) => {
			const described = descriptionBySrc?.get(attributeValue(tag, "src"))
			const text = described || attributeValue(tag, "alt")
			return text ? ` [image: ${text}] ` : " [image] "
		})
		.replaceAll(/<[^>]*>/g, " ")
		.replaceAll(/\s+/g, " ")
		.trim()
}
